import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import {
  normalizeHeroColor,
  normalizeHeroFont,
  normalizeHeroShadow,
  normalizeHeroOverlay,
  normalizeHeroTitleSize,
  normalizeSectionTitleSize,
  normalizeTextWeight,
  normalizeSectionShadow,
  normalizeSectionFont,
  normalizeShadowColor,
  DEFAULT_HERO_TEXT_COLOR,
  DEFAULT_HERO_TEXT_SHADOW,
  DEFAULT_HERO_TEXT_FONT,
  DEFAULT_HERO_OVERLAY_INTENSITY,
  DEFAULT_HERO_TITLE_SIZE,
  DEFAULT_HERO_TITLE_WEIGHT,
  DEFAULT_SECTION_TITLE_SIZE,
  DEFAULT_SECTION_TITLE_WEIGHT,
  DEFAULT_SECTION_TEXT_COLOR,
  DEFAULT_SHADOW_COLOR,
  type HeroTextFont,
  type HeroTextShadow,
  type HeroOverlayIntensity,
  type HeroTitleSize,
  type SectionTitleSize,
  type TextWeight,
} from '@/services/heroText';

export interface CompanySettings {
  companyName: string;
  // Logo real de la marca (imagen) que reemplaza el sello circular "PCP" en
  // comprobantes y guías de envío. Vacío = se usa el sello por defecto.
  companyLogoUrl: string;
  companyCity: string;
  companyAddress: string;
  // Datos del remitente que aparecen junto a los del cliente en la guía de envío.
  senderName: string;
  senderId: string;
  senderPhone: string;
  senderAddress: string;
  senderCity: string;
  distributorDiscountPercent: number;
  sellerCommissionPercent: number;
  donationPercent: number;
  shippingDefaultCost: number;
  contactWhatsapp: string;
  enablePayOnDelivery: boolean;
  enableBankTransfer: boolean;
  // Recargo porcentual por pago contraentrega (0 = sin recargo).
  codSurchargePercent: number;
}

// Contenido editable de la página de inicio (Home) pública. Se edita desde
// /admin/configuracion > "Contenido del Home" y se guarda en company_settings.
export interface LandingContent {
  heroTitle: string;
  heroSubtitle: string;
  heroMediaType: 'none' | 'image' | 'video';
  heroMediaUrl: string;
  // Estilo editable del texto de la portada (color, sombra y tipografía).
  heroTextColor: string;
  heroTextShadow: HeroTextShadow;
  heroTextShadowColor: string;
  heroTextFont: HeroTextFont;
  // Oscurecido del fondo de la portada y tamaño/grosor del título.
  heroOverlayIntensity: HeroOverlayIntensity;
  heroTitleSize: HeroTitleSize;
  heroTitleWeight: TextWeight;
  // Estilo editable de los títulos de sección (Referencias y Propósito).
  sectionTextColor: string;
  sectionTextShadow: HeroTextShadow;
  sectionTextShadowColor: string;
  sectionTextFont: HeroTextFont;
  sectionTitleSize: SectionTitleSize;
  sectionTitleWeight: TextWeight;
  referenciasTitle: string;
  referenciasSubtitle: string;
  referenciasMediaType: 'none' | 'image' | 'video';
  referenciasMediaUrl: string;
  propositoTitle: string;
  propositoText: string;
  propositoMediaType: 'none' | 'image' | 'video';
  propositoMediaUrl: string;
  ctaTitle: string;
  ctaSubtitle: string;
  footerText: string;
  contactEmail: string;
  contactCity: string;
  contactAddress: string;
  facebookUrl: string;
  instagramUrl: string;
  tiktokUrl: string;
}

export const DEFAULT_LANDING_CONTENT: LandingContent = {
  heroTitle: 'Cada paso deja una huella',
  heroSubtitle:
    'Babuchas con diseños únicos de perros y animales. Comodidad, estilo y un propósito: apoyar a quienes más nos necesitan.',
  heroMediaType: 'none',
  heroMediaUrl: '',
  heroTextColor: DEFAULT_HERO_TEXT_COLOR,
  heroTextShadow: DEFAULT_HERO_TEXT_SHADOW,
  heroTextShadowColor: DEFAULT_SHADOW_COLOR,
  heroTextFont: DEFAULT_HERO_TEXT_FONT,
  heroOverlayIntensity: DEFAULT_HERO_OVERLAY_INTENSITY,
  heroTitleSize: DEFAULT_HERO_TITLE_SIZE,
  heroTitleWeight: DEFAULT_HERO_TITLE_WEIGHT,
  sectionTextColor: DEFAULT_SECTION_TEXT_COLOR,
  sectionTextShadow: DEFAULT_HERO_TEXT_SHADOW,
  sectionTextShadowColor: DEFAULT_SHADOW_COLOR,
  sectionTextFont: DEFAULT_HERO_TEXT_FONT,
  sectionTitleSize: DEFAULT_SECTION_TITLE_SIZE,
  sectionTitleWeight: DEFAULT_SECTION_TITLE_WEIGHT,
  referenciasTitle: 'Nuestras Referencias',
  referenciasSubtitle:
    'Cada diseño está inspirado en razas reales. Hechos con materiales premium para tu comodidad diaria.',
  referenciasMediaType: 'none',
  referenciasMediaUrl: '',
  propositoTitle: 'Paticas que transforman vidas',
  propositoText:
    'En PCP creemos que cada paso cuenta. Por eso, el 2% de cada venta se destina a fundaciones y organizaciones que trabajan por el bienestar de los animales en situación de vulnerabilidad.',
  propositoMediaType: 'none',
  propositoMediaUrl: '',
  ctaTitle: '¿Listo para usar tus paticas con propósito?',
  ctaSubtitle:
    'Cada par que compras transforma vidas. La tuya, con comodidad. La de un animal, con esperanza.',
  footerText:
    'Babuchas con diseños únicos inspirados en perros y animales. Cada par apoya fundaciones de bienestar animal.',
  contactEmail: 'hola@paticasconproposito.com',
  contactCity: 'Bogotá, Colombia',
  contactAddress: '',
  facebookUrl: '',
  instagramUrl: '',
  tiktokUrl: '',
};

function normalizeMediaType(value: unknown): LandingContent['heroMediaType'] {
  return value === 'image' || value === 'video' ? value : 'none';
}

// Consulta el contenido público de la Home. Solo lee columnas seguras
// (whitelist) para que esta función pueda usarse en la tienda pública sin
// exponer llaves privadas.
export async function getLandingContent(): Promise<LandingContent> {
  if (!isSupabaseConfigured || !supabase) {
    return { ...DEFAULT_LANDING_CONTENT };
  }

  const { data, error } = await supabase
    .from('company_settings_public')
    .select(
      'hero_title, hero_subtitle, hero_media_type, hero_media_url, hero_text_color, hero_text_shadow, hero_text_shadow_color, hero_text_font, hero_overlay_intensity, hero_title_size, hero_title_weight, section_text_color, section_text_shadow, section_text_shadow_color, section_text_font, section_title_size, section_title_weight, referencias_title, referencias_subtitle, referencias_media_type, referencias_media_url, proposito_title, proposito_text, proposito_media_type, proposito_media_url, cta_title, cta_subtitle, footer_text, contact_email, company_city, company_address, facebook_url, instagram_url, tiktok_url',
    )
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    console.warn('[finances] landing content query failed, using defaults', error?.message);
    return { ...DEFAULT_LANDING_CONTENT };
  }

  const row = data as Record<string, any>;
  return {
    heroTitle: row.hero_title ?? DEFAULT_LANDING_CONTENT.heroTitle,
    heroSubtitle: row.hero_subtitle ?? DEFAULT_LANDING_CONTENT.heroSubtitle,
    heroMediaType: normalizeMediaType(row.hero_media_type),
    heroMediaUrl: row.hero_media_url ?? '',
    heroTextColor: normalizeHeroColor(row.hero_text_color) ?? DEFAULT_LANDING_CONTENT.heroTextColor,
    heroTextShadow: normalizeHeroShadow(row.hero_text_shadow),
    heroTextShadowColor: normalizeShadowColor(row.hero_text_shadow_color),
    heroTextFont: normalizeHeroFont(row.hero_text_font),
    heroOverlayIntensity: normalizeHeroOverlay(row.hero_overlay_intensity),
    heroTitleSize: normalizeHeroTitleSize(row.hero_title_size),
    heroTitleWeight: normalizeTextWeight(row.hero_title_weight),
    sectionTextColor: normalizeHeroColor(row.section_text_color) ?? DEFAULT_SECTION_TEXT_COLOR,
    sectionTextShadow: normalizeSectionShadow(row.section_text_shadow),
    sectionTextShadowColor: normalizeShadowColor(row.section_text_shadow_color),
    sectionTextFont: normalizeSectionFont(row.section_text_font),
    sectionTitleSize: normalizeSectionTitleSize(row.section_title_size),
    sectionTitleWeight: normalizeTextWeight(row.section_title_weight),
    referenciasTitle: row.referencias_title ?? DEFAULT_LANDING_CONTENT.referenciasTitle,
    referenciasSubtitle: row.referencias_subtitle ?? DEFAULT_LANDING_CONTENT.referenciasSubtitle,
    referenciasMediaType: normalizeMediaType(row.referencias_media_type),
    referenciasMediaUrl: row.referencias_media_url ?? '',
    propositoTitle: row.proposito_title ?? DEFAULT_LANDING_CONTENT.propositoTitle,
    propositoText: row.proposito_text ?? DEFAULT_LANDING_CONTENT.propositoText,
    propositoMediaType: normalizeMediaType(row.proposito_media_type),
    propositoMediaUrl: row.proposito_media_url ?? '',
    ctaTitle: row.cta_title ?? DEFAULT_LANDING_CONTENT.ctaTitle,
    ctaSubtitle: row.cta_subtitle ?? DEFAULT_LANDING_CONTENT.ctaSubtitle,
    footerText: row.footer_text ?? DEFAULT_LANDING_CONTENT.footerText,
    contactEmail: row.contact_email ?? DEFAULT_LANDING_CONTENT.contactEmail,
    contactCity: row.company_city ?? DEFAULT_LANDING_CONTENT.contactCity,
    contactAddress: row.company_address ?? '',
    facebookUrl: row.facebook_url ?? '',
    instagramUrl: row.instagram_url ?? '',
    tiktokUrl: row.tiktok_url ?? '',
  };
}

export interface FinanceSummary {
  todaySalesTotal: number;
  monthSalesTotal: number;
  totalCommissions: number; // % comisión sobre subtotal de ventas confirmadas
  totalDonations: number; // % donación sobre subtotal de ventas confirmadas
  accumulatedDonations: number; // fondo acumulado desde la tabla donations
  totalStock: number; // unidades totales (disponible + reservado + consignado)
  inventoryValue: number; // valor retail del inventario
  inventoryCost: number; // costo del inventario
  saleCount: number;
  commissionPercent: number; // % efectivo aplicado (desde company_settings)
  donationPercent: number; // % efectivo aplicado (desde company_settings)
  totalExpenses: number; // suma de gastos operativos
  totalPayments: number; // suma neta de pagos registrados
  movementCount: number; // total de movimientos de inventario
  shippingOperationalCost: number; // flete cobrado en ventas confirmadas (único flete que se resta del capital)
  grossSalesTotal: number; // ingreso bruto (ventas confirmadas, sin descontar costo)
  cogsTotal: number; // costo de lo vendido (COGS) de las ventas confirmadas
  marketingInvestment: number; // inversión consolidada de marketing (campañas cerradas)
  capitalNeto: number; // capital neto (inventario a costo + ingreso bruto − deducciones)
  pendingCommissions: number; // comisiones generadas aún NO pagadas (se deben; informativo)
  refundedReturns: number; // devoluciones reembolsadas (status refunded); se restan del capital
  shippingCollected: number; // flete cobrado al cliente (informativo)
  premiumShippingAbsorbed: number; // flete premium pagado a la transportadora y no cobrado
  shippingPaidCost: number; // costo real de envíos pagado (cobrado + premium absorbido)
  shippingMargin: number; // margen del flete (cobrado − pagado); negativo = te cuesta
  codSurchargeCollected: number; // recargo por contra entrega cobrado al cliente (ya incluido en el ingreso bruto)
  codFeesCost: number; // comisión real pagada a la transportadora por recaudo contra entrega (se resta del capital)
}

export interface FinancesResult {
  data: FinanceSummary;
}

// Fallback seguro si company_settings no existe o la consulta falla.
const DEFAULT_COMMISSION_PERCENT = 5;
const DEFAULT_DONATION_PERCENT = 2;
const DEFAULT_DISTRIBUTOR_DISCOUNT_PERCENT = 37;

// Canal oficial de contacto PCP (WhatsApp). Es el fallback único cuando la
// fila company_settings no registra un número. Fuente de verdad del canal.
export const PCP_OFFICIAL_WHATSAPP = '+57 300 123 4567';

function datePrefix(dateStr: string, length: number): string {
  return String(dateStr).slice(0, length);
}

function isToday(dateStr: string): boolean {
  return datePrefix(dateStr, 10) === new Date().toISOString().slice(0, 10);
}
function isCurrentMonth(dateStr: string): boolean {
  return datePrefix(dateStr, 7) === new Date().toISOString().slice(0, 7);
}

// Consulta la configuración central de la empresa. Es la única fuente de
// verdad de los porcentajes de comisión, donación y descuento distribuidor.
// ⚠️ SEGURIDAD: la lista de columnas es una whitelist explícita. NUNCA uses
// `select('*')` aquí ni añadas llaves privadas (p. ej. `resend_api_key`): esta
// función es consumida por componentes del catálogo público.
export async function getCompanySettings(): Promise<CompanySettings> {
  const fallback: CompanySettings = {
    companyName: 'PCP - Paticas con Propósito',
    companyLogoUrl: '',
    companyCity: 'Bogotá, Colombia',
    companyAddress: '',
    senderName: '',
    senderId: '',
    senderPhone: '',
    senderAddress: '',
    senderCity: '',
    distributorDiscountPercent: DEFAULT_DISTRIBUTOR_DISCOUNT_PERCENT,
    sellerCommissionPercent: DEFAULT_COMMISSION_PERCENT,
    donationPercent: DEFAULT_DONATION_PERCENT,
    shippingDefaultCost: 0,
    contactWhatsapp: PCP_OFFICIAL_WHATSAPP,
    enablePayOnDelivery: true,
    enableBankTransfer: true,
    codSurchargePercent: 3,
  };

  if (!isSupabaseConfigured || !supabase) {
    return fallback;
  }

  const { data, error } = await supabase
    .from('company_settings_public')
    .select(
      'company_name, company_logo_url, company_city, company_address, sender_name, sender_id, sender_phone, sender_address, sender_city, distributor_discount_percent, seller_commission_percent, donation_percent, shipping_default_cost, contact_whatsapp, enable_pay_on_delivery, enable_bank_transfer, cod_surcharge_percent',
    )
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    console.warn('[finances] company_settings query failed, using safe defaults', error?.message);
    return fallback;
  }

  const row = data as Record<string, any>;
  return {
    companyName: row.company_name ?? 'PCP - Paticas con Propósito',
    companyLogoUrl: row.company_logo_url ?? '',
    companyCity: row.company_city ?? 'Bogotá, Colombia',
    companyAddress: row.company_address ?? '',
    senderName: row.sender_name ?? '',
    senderId: row.sender_id ?? '',
    senderPhone: row.sender_phone ?? '',
    senderAddress: row.sender_address ?? '',
    senderCity: row.sender_city ?? '',
    distributorDiscountPercent: Number(
      row.distributor_discount_percent ?? DEFAULT_DISTRIBUTOR_DISCOUNT_PERCENT,
    ),
    sellerCommissionPercent: Number(
      row.seller_commission_percent ?? DEFAULT_COMMISSION_PERCENT,
    ),
    donationPercent: Number(row.donation_percent ?? DEFAULT_DONATION_PERCENT),
    shippingDefaultCost: Number(row.shipping_default_cost ?? 0),
    contactWhatsapp: row.contact_whatsapp ?? PCP_OFFICIAL_WHATSAPP,
    enablePayOnDelivery: row.enable_pay_on_delivery ?? true,
    enableBankTransfer: row.enable_bank_transfer ?? true,
    codSurchargePercent: Number(row.cod_surcharge_percent ?? 3),
  };
}

// Configuración del botón flotante de WhatsApp de la tienda pública.
export interface WhatsappButtonSettings {
  enabled: boolean;
  bubbleText: string;
  whatsapp: string;
}

export const DEFAULT_WHATSAPP_BUTTON: WhatsappButtonSettings = {
  enabled: true,
  bubbleText: '¿Tienes dudas? ¡Escríbenos!',
  whatsapp: PCP_OFFICIAL_WHATSAPP,
};

// Consulta la configuración del botón flotante de WhatsApp (whitelist pública).
export async function getWhatsappButtonSettings(): Promise<WhatsappButtonSettings> {
  if (!isSupabaseConfigured || !supabase) return { ...DEFAULT_WHATSAPP_BUTTON };
  try {
    const { data, error } = await supabase
      .from('company_settings_public')
      .select('enable_whatsapp_button, whatsapp_bubble_text, contact_whatsapp')
      .limit(1)
      .maybeSingle();
    if (error || !data) return { ...DEFAULT_WHATSAPP_BUTTON };
    const row = data as Record<string, any>;
    return {
      enabled: row.enable_whatsapp_button ?? DEFAULT_WHATSAPP_BUTTON.enabled,
      bubbleText: row.whatsapp_bubble_text ?? DEFAULT_WHATSAPP_BUTTON.bubbleText,
      whatsapp: row.contact_whatsapp ?? DEFAULT_WHATSAPP_BUTTON.whatsapp,
    };
  } catch (err) {
    console.warn('[finances] whatsapp button settings failed:', err);
    return { ...DEFAULT_WHATSAPP_BUTTON };
  }
}

export interface DonationWidgetSettings {
  enableDonationDog: boolean;
  bannerText: string;
  foundationName: string;
  foundationDesc: string;
  foundationUrl: string;
  whatsapp: string;
  bankDetails: string;
  thankyouText: string;
  // Imagen propia del perrito (opcional). Reemplaza el emoji 🐶 cuando tiene
  // valor. Vacío = se usa el emoji por defecto.
  donationDogImageUrl: string;
}

export const DEFAULT_DONATION_WIDGET: DonationWidgetSettings = {
  enableDonationDog: true,
  bannerText: '¿Quieres ayudar a la Patyfamilia? ¡Dona aquí! 🐾',
  foundationName: 'Patyfamilia',
  foundationDesc: 'Ayúdanos a cuidar y rescatar perros en situación de vulnerabilidad.',
  foundationUrl: '',
  whatsapp: '',
  bankDetails: '',
  thankyouText: '¡Gracias por ayudarnos! Ya eres parte de la Patyfamilia 🐶❤️',
  donationDogImageUrl: '',
};

// Consulta la configuración pública del perrito de donaciones. Solo lee
// columnas seguras (whitelist) para poder usarse en la tienda pública.
export async function getDonationWidgetSettings(): Promise<DonationWidgetSettings> {
  if (!isSupabaseConfigured || !supabase) {
    return { ...DEFAULT_DONATION_WIDGET };
  }

  const { data, error } = await supabase
    .from('company_settings_public')
    .select(
      'enable_donation_dog, donation_banner_text, donation_foundation_name, donation_foundation_desc, donation_foundation_url, donation_whatsapp, donation_bank_details, donation_thankyou_text, donation_dog_image_url',
    )
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    console.warn('[finances] donation widget settings query failed', error?.message);
    return { ...DEFAULT_DONATION_WIDGET };
  }

  const row = data as Record<string, any>;
  return {
    enableDonationDog: row.enable_donation_dog ?? DEFAULT_DONATION_WIDGET.enableDonationDog,
    bannerText: row.donation_banner_text ?? DEFAULT_DONATION_WIDGET.bannerText,
    foundationName: row.donation_foundation_name ?? DEFAULT_DONATION_WIDGET.foundationName,
    foundationDesc: row.donation_foundation_desc ?? DEFAULT_DONATION_WIDGET.foundationDesc,
    foundationUrl: row.donation_foundation_url ?? DEFAULT_DONATION_WIDGET.foundationUrl,
    whatsapp: row.donation_whatsapp ?? DEFAULT_DONATION_WIDGET.whatsapp,
    bankDetails: row.donation_bank_details ?? DEFAULT_DONATION_WIDGET.bankDetails,
    thankyouText: row.donation_thankyou_text ?? DEFAULT_DONATION_WIDGET.thankyouText,
    donationDogImageUrl: row.donation_dog_image_url ?? DEFAULT_DONATION_WIDGET.donationDogImageUrl,
  };
}

// Avisos configurables de la tienda (catálogo y checkout). Se editan desde
// /admin/configuracion y se leen desde la vista pública (whitelist).
export interface StoreNotices {
  catalogEnabled: boolean;
  catalogText: string;
  checkoutEnabled: boolean;
  checkoutText: string;
}

export const DEFAULT_CATALOG_NOTICE =
  '¿No tenemos en stock la babucha que quieres? ¡Tranquilo! Puedes agregarla al carro y se mandará a fabricación especialmente para ti.';

export const DEFAULT_CHECKOUT_NOTICE = [
  '⚠️ Información Importante para tu Pedido:',
  '',
  '1. Tiempos de Entrega / Producción: Si la referencia Premium seleccionada no cuenta con stock disponible de entrega inmediata en la página, tu babucha se mandará a fabricar y el envío demorará un poco más de lo habitual.',
  '',
  '2. Variación de Tonos: Los tonos del material o insumos pueden presentar ligeras variaciones respecto a las fotos debido a los lotes de fabricación de los insumos.',
  '',
  '3. Línea Sencilla hasta Agotar Existencias: Las referencias de babuchas sencillas se encuentran disponibles únicamente por tiempo limitado y hasta agotar existencias (no se fabrican sobre pedido).',
].join('\n');

export const DEFAULT_STORE_NOTICES: StoreNotices = {
  catalogEnabled: true,
  catalogText: DEFAULT_CATALOG_NOTICE,
  checkoutEnabled: true,
  checkoutText: DEFAULT_CHECKOUT_NOTICE,
};

// Consulta los avisos públicos configurables de la tienda. Solo lee columnas
// seguras (whitelist) para poder usarse en la tienda pública.
export async function getStoreNotices(): Promise<StoreNotices> {
  if (!isSupabaseConfigured || !supabase) {
    return { ...DEFAULT_STORE_NOTICES };
  }
  try {
    const { data, error } = await supabase
      .from('company_settings_public')
      .select('notice_catalog_enabled, notice_catalog_text, notice_checkout_enabled, notice_checkout_text')
      .limit(1)
      .maybeSingle();
    if (error || !data) return { ...DEFAULT_STORE_NOTICES };
    const row = data as Record<string, any>;
    return {
      catalogEnabled: row.notice_catalog_enabled ?? DEFAULT_STORE_NOTICES.catalogEnabled,
      catalogText: row.notice_catalog_text || DEFAULT_STORE_NOTICES.catalogText,
      checkoutEnabled: row.notice_checkout_enabled ?? DEFAULT_STORE_NOTICES.checkoutEnabled,
      checkoutText: row.notice_checkout_text || DEFAULT_STORE_NOTICES.checkoutText,
    };
  } catch (err) {
    console.warn('[finances] store notices query failed:', err);
    return { ...DEFAULT_STORE_NOTICES };
  }
}

function buildEmptySummary(settings: CompanySettings): FinanceSummary {
  return {
    todaySalesTotal: 0,
    monthSalesTotal: 0,
    totalCommissions: 0,
    totalDonations: 0,
    accumulatedDonations: 0,
    totalStock: 0,
    inventoryValue: 0,
    inventoryCost: 0,
    saleCount: 0,
    commissionPercent: settings.sellerCommissionPercent,
    donationPercent: settings.donationPercent,
    totalExpenses: 0,
    totalPayments: 0,
    movementCount: 0,
    shippingOperationalCost: 0,
    grossSalesTotal: 0,
    cogsTotal: 0,
    marketingInvestment: 0,
    capitalNeto: 0,
    pendingCommissions: 0,
    refundedReturns: 0,
    shippingCollected: 0,
    premiumShippingAbsorbed: 0,
    shippingPaidCost: 0,
    shippingMargin: 0,
    codSurchargeCollected: 0,
    codFeesCost: 0,
  };
}

// Calcula y consulta los KPIs financieros consolidados desde Supabase.
export async function fetchFinances(): Promise<FinancesResult> {
  // Tasas dinámicas desde company_settings (única fuente de verdad).
  const settings = await getCompanySettings();
  if (!isSupabaseConfigured || !supabase) {
    return { data: buildEmptySummary(settings) };
  }

  try {
    const [
      salesRes,
      donationsRes,
      commissionsRes,
      invRes,
      productsRes,
      variantsRes,
      paymentsRes,
      expensesRes,
      movementsRes,
      campaignsRes,
      ordersRes,
      returnsRes,
    ] = await Promise.all([
      supabase.from('sales').select('id, subtotal, total, cost_total, gross_profit, sale_date, status, voided_at, shipping_cost'),
      supabase.from('donations').select('donation_generated'),
      supabase.from('commissions').select('commission_amount, is_paid'),
      supabase.from('inventory').select('variant_id, available, reserved, consigned'),
      supabase.from('products').select('id, retail_price, unit_cost'),
      supabase.from('product_variants').select('id, product_id'),
      supabase.from('payments').select('id, amount'),
      supabase.from('expenses').select('id, amount'),
      supabase.from('inventory_movements').select('id, movement_type'),
      supabase.from('campaigns').select('status, declared_cost'),
      supabase.from('orders').select('shipping_cost, sale_id, estimated_shipping_cost, status, cod_surcharge, estimated_cod_commission'),
      supabase.from('returns').select('refund_amount, status'),
    ]);

    if (salesRes.error) throw new Error(salesRes.error.message);
    if (donationsRes.error) throw new Error(donationsRes.error.message);
    if (commissionsRes.error) throw new Error(commissionsRes.error.message);
    if (invRes.error) throw new Error(invRes.error.message);
    if (productsRes.error) throw new Error(productsRes.error.message);
    if (variantsRes.error) throw new Error(variantsRes.error.message);
    if (paymentsRes.error) throw new Error(paymentsRes.error.message);
    if (expensesRes.error) throw new Error(expensesRes.error.message);
    if (movementsRes.error) throw new Error(movementsRes.error.message);
    if (campaignsRes.error) throw new Error(campaignsRes.error.message);
    if (ordersRes.error) throw new Error(ordersRes.error.message);
    if (returnsRes.error) throw new Error(returnsRes.error.message);

    const sales = (salesRes.data ?? []) as Array<Record<string, any>>;
    // Aunque no haya ventas, no caemos al mock (sería mostrar datos falsos).
    const confirmed = sales.filter((s) => s.status === 'completed' && !s.voided_at);
    const todayTotal = confirmed.filter((s) => isToday(s.sale_date)).reduce((sum, s) => sum + Number(s.total), 0);
    const monthTotal = confirmed.filter((s) => isCurrentMonth(s.sale_date)).reduce((sum, s) => sum + Number(s.total), 0);

    const accumulatedDonations = ((donationsRes.data ?? []) as Array<Record<string, any>>).reduce(
      (sum, d) => sum + Number(d.donation_generated ?? 0),
      0,
    );

    const totalExpenses = ((expensesRes.data ?? []) as Array<Record<string, any>>).reduce(
      (sum, e) => sum + Number(e.amount ?? 0),
      0,
    );

    const totalPayments = ((paymentsRes.data ?? []) as Array<Record<string, any>>).reduce(
      (sum, p) => sum + Number(p.amount ?? 0),
      0,
    );

    const movementCount = ((movementsRes.data ?? []) as Array<Record<string, any>>).length;

    const variantProductMap = new Map(
      ((variantsRes.data ?? []) as Array<Record<string, any>>).map((v) => [v.id, v.product_id]),
    );
    const productMap = new Map(
      ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p]),
    );

    let totalStock = 0;
    let inventoryValue = 0;
    let inventoryCost = 0;
    ((invRes.data ?? []) as Array<Record<string, any>>).forEach((i) => {
      const productId = variantProductMap.get(i.variant_id);
      const product = productId ? productMap.get(productId) : undefined;
      const qty = Number(i.available) + Number(i.reserved) + Number(i.consigned);
      totalStock += qty;
      if (product) {
        inventoryValue += qty * Number(product.retail_price);
        inventoryCost += qty * Number(product.unit_cost);
      }
    });

    // Solo comisiones YA PAGADAS: el neto refleja lo que realmente salió de
    // caja. Las comisiones pendientes (que se deben) no se descuentan todavía.
    const totalCommissions = ((commissionsRes.data ?? []) as Array<Record<string, any>>)
      .filter((c) => c.is_paid)
      .reduce((sum, c) => sum + Number(c.commission_amount ?? 0), 0);

    // Comisiones pendientes por pagar (lo que se debe a los vendedores). Es un
    // aviso informativo: NO se resta del Capital Neto para no comprometer el
    // capital con dinero que aún no ha salido de caja.
    const pendingCommissions = ((commissionsRes.data ?? []) as Array<Record<string, any>>)
      .filter((c) => !c.is_paid)
      .reduce((sum, c) => sum + Number(c.commission_amount ?? 0), 0);

    // Ingreso bruto (ventas confirmadas) y su costo de lo vendido (COGS).
    const grossSalesTotal = confirmed.reduce((sum, s) => sum + Number(s.total ?? 0), 0);
    const cogsTotal = confirmed.reduce((sum, s) => sum + Number(s.cost_total ?? 0), 0);

    // Inversión consolidada de marketing: solo campañas pausadas/finalizadas
    // (su costo total declarado). El presupuesto tope/control nunca suma.
    const marketingInvestment = ((campaignsRes.data ?? []) as Array<Record<string, any>>)
      .filter((c) => c.status === 'paused' || c.status === 'finished')
      .reduce((sum, c) => sum + Number(c.declared_cost ?? 0), 0);

    // Flete de ventas confirmadas. Es el único flete que se resta del Capital
    // Neto, porque el `total` de esas ventas YA incluye el flete cobrado (se
    // sumó al ingreso bruto). Así el flete queda como passthrough (se cobra y
    // se paga) y no infla ni subestima el capital.
    const salesShipping = confirmed.reduce((sum, s) => sum + Number(s.shipping_cost ?? 0), 0);
    const orderRows = (ordersRes.data ?? []) as Array<Record<string, any>>;
    // Flete de pedidos aún NO facturados (sin sale_id). Es SOLO informativo para
    // el desglose de "envíos cobrados": estos pedidos todavía no son venta, por
    // lo que su flete nunca se sumó al ingreso bruto y NO debe restarse del
    // capital (restarlo sin haberlo sumado subestimaba el Capital Neto).
    const pendingOrderShipping = orderRows
      .filter((o) => !o.sale_id)
      .reduce((sum, o) => sum + Number(o.shipping_cost ?? 0), 0);
    // Costo de envíos que SÍ se resta del Capital Neto = flete de ventas
    // confirmadas (misma base que el ingreso bruto).
    const shippingOperationalCost = salesShipping;

    // Desglose informativo del flete (no modifica el Capital Neto).
    //  · Envíos cobrados = flete que paga el cliente (ventas confirmadas +
    //    pedidos aún no facturados).
    //  · Flete premium absorbido = se paga a la transportadora pero va incluido
    //    en el precio de la babucha premium, por lo que no se cobra.
    const shippingCollected = salesShipping + pendingOrderShipping;
    const premiumShippingAbsorbed = orderRows
      .filter((o) => o.status !== 'cancelled')
      .reduce((sum, o) => sum + Number(o.estimated_shipping_cost ?? 0), 0);
    const shippingPaidCost = shippingCollected + premiumShippingAbsorbed;
    const shippingMargin = shippingCollected - shippingPaidCost;

    // Recargo por contra entrega: se cobra al cliente y va DENTRO del `total`
    // de las ventas confirmadas (ya sumado al ingreso bruto). Su costo asociado
    // es la comisión que cobra la transportadora por recaudar el dinero; ese
    // costo NO estaba restándose en ningún lado, así que el capital quedaba
    // inflado por el recargo. Aquí restamos la comisión de las ventas
    // confirmadas (pedidos ya facturados) para reflejar el flujo real.
    const codSurchargeCollected = orderRows
      .filter((o) => o.sale_id)
      .reduce((sum, o) => sum + Number(o.cod_surcharge ?? 0), 0);
    const codFeesCost = orderRows
      .filter((o) => o.sale_id && o.status !== 'cancelled')
      .reduce((sum, o) => sum + Number(o.estimated_cod_commission ?? 0), 0);

    // Devoluciones reembolsadas (status refunded). Se restan del Capital Neto
    // para alinearlo con el Estado de Resultados de Reportes.
    const refundedReturns = ((returnsRes.data ?? []) as Array<Record<string, any>>)
      .filter((r) => r.status === 'refunded')
      .reduce((sum, r) => sum + Number(r.refund_amount ?? 0), 0);

    // Capital neto = inventario a costo + ingreso bruto − costo de lo vendido
    // − gastos operativos − comisiones pagadas − marketing − donaciones
    // − devoluciones reembolsadas − flete de ventas confirmadas.
    const capitalNeto =
      inventoryCost +
      grossSalesTotal -
      cogsTotal -
      totalExpenses -
      totalCommissions -
      marketingInvestment -
      accumulatedDonations -
      refundedReturns -
      shippingOperationalCost -
      codFeesCost;

    return {
      data: {
        todaySalesTotal: todayTotal,
        monthSalesTotal: monthTotal,
        totalCommissions,
        totalDonations: accumulatedDonations,
        accumulatedDonations,
        totalStock,
        inventoryValue,
        inventoryCost,
        saleCount: confirmed.length,
        commissionPercent: settings.sellerCommissionPercent,
        donationPercent: settings.donationPercent,
        totalExpenses,
        totalPayments,
        movementCount,
        shippingOperationalCost,
        grossSalesTotal,
        cogsTotal,
        marketingInvestment,
        capitalNeto,
        pendingCommissions,
        refundedReturns,
        shippingCollected,
        premiumShippingAbsorbed,
        shippingPaidCost,
        shippingMargin,
        codSurchargeCollected,
        codFeesCost,
      },
    };
  } catch (err) {
    console.error('[finances] Supabase connection failed:', err);
    throw err;
  }
}